import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Stethoscope, ArrowRight, Flame, Clock, Star, ChevronRight, Dumbbell } from "lucide-react";
import { api, openWhatsApp } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ProductImage } from "@/components/ProductImage";
import { BrandMark } from "@/components/BrandMark";

export default function Home() {
  const navigate = useNavigate();
  const { user, isGuest } = useApp();
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active))).catch(() => {});
    api.get("/recipes").then((r) => setRecipes(r.data.filter((x) => x.active !== false))).catch(() => {});
  }, []);

  const firstName = user?.name?.split(" ")[0] || (isGuest ? "Guest" : "there");
  const initials = (user?.name || "G U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="animate-fade-in">
      {/* Greeting bar */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <button data-testid="home-profile-btn" onClick={() => navigate("/profile")} className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="avatar" className="w-11 h-11 rounded-2xl object-cover ring-2 ring-emerald-500/30" />
          ) : (
            <div className="w-11 h-11 rounded-2xl bg-emerald-700 text-white flex items-center justify-center font-bold text-sm">{initials}</div>
          )}
          <div className="text-left">
            <p className="text-xs text-slate-400 dark:text-slate-500">Hello, 👋</p>
            <p className="text-base font-bold text-emerald-950 dark:text-emerald-50 font-head leading-tight">{firstName}</p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <BrandMark size={44} className="border border-slate-100 dark:border-emerald-800 shadow-none" />
          <button data-testid="home-notification-btn" className="relative w-11 h-11 rounded-2xl bg-white dark:bg-emerald-900 border border-slate-100 dark:border-emerald-800 flex items-center justify-center text-slate-600 dark:text-slate-300">
            <Bell className="w-5 h-5" />
            <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-emerald-900" />
          </button>
        </div>
      </div>

      {/* Hero banner */}
      <div className="px-4 mt-2">
        <div data-testid="home-hero" className="relative overflow-hidden rounded-3xl border border-[#e4ead9] dark:border-emerald-800 bg-gradient-to-br from-[#fbf8ef] via-[#f3f6ea] to-[#d9e6d0] dark:from-emerald-900 dark:via-emerald-900 dark:to-emerald-800">
          <img
            src="/images/brand/hero.jpg"
            alt="U&URS Nutritionist — Eat Right, Live Bright"
            data-testid="home-hero-photo"
            className="block w-full h-auto select-none pointer-events-none"
          />

          <div className="relative z-10 px-5 pt-4 pb-5">
            <span className="text-[10px] font-bold tracking-wider uppercase text-[#a6823a] dark:text-amber-300">Clinical Wellness</span>
            <h2 className="mt-1.5 text-xl font-extrabold leading-snug font-head text-emerald-950 dark:text-emerald-50">
              Transform Your Health with <span className="text-emerald-700 dark:text-emerald-300">Clinical Precision</span>
            </h2>
            <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">Personalized Nutrition & Wellness Guidance</p>
            <button
              data-testid="home-hero-cta"
              onClick={() => navigate("/booking")}
              className="mt-3.5 inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm px-4 py-2.5 rounded-xl shadow-lg shadow-emerald-700/25 transition"
            >
              Book Consultation <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Two action cards */}
      <div className="px-4 mt-5 grid grid-cols-2 gap-3">
        <button
          data-testid="home-general-guidance-card"
          onClick={() => navigate("/guide")}
          className="text-left p-4 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 hover:shadow-md transition active:scale-[0.98]"
        >
          <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center mb-3">
            <Stethoscope className="w-5 h-5 text-emerald-700 dark:text-emerald-300" />
          </div>
          <p className="font-bold text-sm text-emerald-950 dark:text-emerald-50">General Guidance</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Free condition diets</p>
        </button>
        <button
          data-testid="home-consultation-card"
          onClick={() => navigate("/booking")}
          className="text-left p-4 rounded-2xl bg-emerald-700 dark:bg-emerald-600 text-white hover:shadow-md transition active:scale-[0.98]"
        >
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center mb-3">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <p className="font-bold text-sm">Customized Consult</p>
          <p className="text-xs text-emerald-100 mt-0.5">Personalized plans</p>
        </button>
      </div>

      {/* Exercise shortcut */}
      <div className="px-4 mt-3">
        <button
          data-testid="home-exercise-card"
          onClick={() => navigate("/exercise")}
          className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 active:scale-[0.99] transition"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-400/90 flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-amber-950" />
          </div>
          <div className="text-left flex-1">
            <p className="font-bold text-sm text-amber-900 dark:text-amber-200">Exercise & Fitness Guidance</p>
            <p className="text-xs text-amber-700/70 dark:text-amber-300/60">50 guided exercises for every goal</p>
          </div>
          <ChevronRight className="w-5 h-5 text-amber-700 dark:text-amber-400" />
        </button>
      </div>

      {/* Featured products */}
      <Section title="Featured Products" onSeeAll={() => navigate("/products")} />
      <div className="flex gap-3 overflow-x-auto scrollbar-none px-4 pb-1">
        {products.slice(0, 5).map((p) => (
          <button
            key={p.id}
            data-testid={`home-product-${p.id}`}
            onClick={() => navigate(`/products/${p.id}`)}
            className="shrink-0 w-36 text-left bg-white dark:bg-emerald-900/60 rounded-2xl border border-slate-100 dark:border-emerald-800 overflow-hidden active:scale-[0.98] transition"
          >
            <div className="aspect-square bg-slate-100 dark:bg-emerald-800 overflow-hidden">
              <ProductImage product={p} />
            </div>
            <div className="p-2.5">
              <p className="text-xs font-semibold text-emerald-950 dark:text-emerald-50 line-clamp-2 leading-snug h-8">{p.name}</p>
              <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400 mt-1">₹{p.price.toLocaleString("en-IN")}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Trending recipes */}
      <Section title="Trending Recipes" onSeeAll={() => navigate("/recipes")} />
      <div className="flex gap-3 overflow-x-auto scrollbar-none px-4 pb-2">
        {recipes.slice(0, 6).map((r) => (
          <button
            key={r.id}
            data-testid={`home-recipe-${r.id}`}
            onClick={() => navigate(`/recipes/${r.id}`)}
            className="shrink-0 w-44 text-left bg-white dark:bg-emerald-900/60 rounded-2xl border border-slate-100 dark:border-emerald-800 overflow-hidden active:scale-[0.98] transition"
          >
            <div className="h-24 bg-slate-100 dark:bg-emerald-800 overflow-hidden">
              <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-2.5">
              <p className="text-xs font-semibold text-emerald-950 dark:text-emerald-50 line-clamp-1">{r.name}</p>
              <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.total_time}</span>
                <span className="flex items-center gap-1"><Flame className="w-3 h-3" />{r.nutrition?.Calories}</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const Section = ({ title, onSeeAll }) => (
  <div className="px-5 mt-6 mb-3 flex items-center justify-between">
    <h3 className="text-base font-bold text-emerald-950 dark:text-emerald-50 font-head">{title}</h3>
    <button onClick={onSeeAll} className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
      See all <ChevronRight className="w-3.5 h-3.5" />
    </button>
  </div>
);
